import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import FAQ from "./faq";
import Gallery from "./Gallery";
import LiteYouTube from "./LiteYouTube";
import { GROUPS } from '../data/squadrons';
import { trackJoinDiscord } from '../lib/analytics';

// Hook: fetch upcoming Discord events
const useEvents = () => {
  const [events, setEvents] = useState([]);
  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch('/api/events');
      if (res.ok) setEvents(await res.json());
    } catch { /* silent fail — section just won't show */ }
  }, []);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  return events;
};

// Lazy-load wrapper: renders children only when scrolled near viewport
const LazyLoad = ({ children, rootMargin = '200px' }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { rootMargin }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [rootMargin]);
  return <div ref={ref}>{visible ? children : <div style={{ minHeight: 500 }} />}</div>;
};


// Reusable section heading
const SectionHeading = ({ children, subtitle }) => (
  <div className="mb-10">
    <h2 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight uppercase text-hvy-900">
      {children}
    </h2>
    {subtitle && <p className="mt-3 text-lg text-gray-600 max-w-3xl">{subtitle}</p>}
    <div className="mt-4 h-1 w-16 bg-hvy-accent rounded-full" />
  </div>
);

// Squadron card
const SquadronCard = ({ img, alt, name, airframe }) => (
  <div className="group text-center bg-white/60 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-200/50 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
    <img
      className="mx-auto h-40 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
      src={img}
      alt={alt}
      loading="lazy"
    />
    <h4 className="mt-4 text-base font-semibold text-gray-900">{name}</h4>
    <p className="text-sm text-hvy-500 font-medium">{airframe}</p>
  </div>
);

// Event card with expandable description
const EventCard = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const date = new Date(event.start);
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  return (
    <div
      className={`bg-white/60 backdrop-blur-sm rounded-xl p-5 shadow-sm border border-gray-200/50 hover:shadow-md transition-shadow duration-300${event.description ? ' cursor-pointer' : ''}`}
      onClick={() => event.description && setExpanded((v) => !v)}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-14 text-center bg-hvy-500/10 rounded-lg py-2 px-1">
          <div className="text-xs font-semibold uppercase text-hvy-500 tracking-wider">
            {date.toLocaleDateString('en-US', { weekday: 'short' })}
          </div>
          <div className="text-2xl font-bold text-hvy-900 leading-tight">
            {date.getDate()}
          </div>
          <div className="text-xs text-gray-500 uppercase">
            {date.toLocaleDateString('en-US', { month: 'short' })}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-semibold text-gray-900 truncate">{event.name}</h4>
          <p className="text-sm text-hvy-500 font-medium mt-0.5">{time}</p>
          {event.description && (
            <p className={`text-sm text-gray-500 mt-1 transition-all duration-300${expanded ? '' : ' line-clamp-2'}`}>{event.description}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const MainContent = () => {
  const events = useEvents();
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > window.innerHeight);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div>
      {/* ═══════════════ HERO SECTION ═══════════════ */}
      <section id="top" className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background image */}
        <img
          src="pics/optimized/banner.webp"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-hvy-900/70 via-hvy-900/50 to-hvy-900/80" />

        {/* Hero content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <img
            src="/logo.webp"
            alt="JTF Heavy"
            className="mx-auto h-28 lg:h-36 mb-8 drop-shadow-2xl animate-fade-in"
          />
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-wider drop-shadow-lg animate-fade-in-up hero-title-gradient">
            Joint Task Force Heavy
          </h1>
          <p className="mt-4 text-lg md:text-xl text-gray-300 font-light tracking-wide animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Global DCS Community &bull; Campaign Missions &bull; Weekly Flights
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <a
              href="#about-us"
              className="px-8 py-3 bg-hvy-accent hover:bg-hvy-gold text-hvy-900 font-bold uppercase tracking-wider rounded-lg transition-colors duration-200 shadow-lg"
            >
              Learn More
            </a>
            <a
              href="#join-discord"
              className="px-8 py-3 border-2 border-white/30 hover:border-white/60 text-white font-bold uppercase tracking-wider rounded-lg transition-colors duration-200"
            >
              Join Us
            </a>
          </div>
        </div>

      </section>

      {/* ═══════════════ MAIN CONTENT AREA ═══════════════ */}
      <main className="w-full px-6 py-16 bg-gray-50 bg-[url('bg.webp')] bg-repeat bg-fixed bg-top">
        <div className="max-w-7xl mx-auto">

          {/* ─── UPCOMING EVENTS ─── */}
          {events.length > 0 && (
            <section className="py-16">
              <SectionHeading>Upcoming Events</SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </section>
          )}

          {/* ─── VIDEO SHOWCASE ─── */}
          <section className="py-16">
            <SectionHeading>Watch Us Fly</SectionHeading>
            <div className="max-w-4xl mx-auto">
              <div className="relative w-full rounded-xl overflow-hidden shadow-2xl border border-gray-200/50" style={{ paddingBottom: '56.25%' }}>
                <LiteYouTube id="pzUfgtTtcF4" title="Joint Task Force Heavy" />
              </div>
            </div>
          </section>

          {/* ─── ABOUT US ─── */}
          <section id="about-us" className="py-16 scroll-mt-20">
            <div className="flex flex-wrap lg:flex-nowrap items-center gap-12">
              {/* Text */}
              <div className="w-full lg:w-2/3">
                <SectionHeading>About Us</SectionHeading>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Joint Task Force Heavy (HVY) is a global Digital Combat Simulator community with pilots from diverse backgrounds, including casual gamers, experienced commercial aviators, and former military pilots. Representing nearly every continent, HVY fields squadrons operating a full range of NATO aircraft. The group conducts regular squadron operations and dynamic, modern-era campaigns. No prior experience in a jet is required, as HVY members are always eager to assist and support one another. While the community spans multiple time zones, most operations and flight nights are scheduled around North American time zones (EST and PST).
                </p>

                {/* Stat bar */}
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { value: '8', label: 'Squadrons' },
                    { value: '6', label: 'Continents' },
                    { value: 'Weekly', label: 'Flights' },
                    { value: 'NATO', label: 'Operations' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-gray-200/50">
                      <div className="font-heading text-3xl font-bold text-hvy-500">{stat.value}</div>
                      <div className="text-sm text-gray-500 uppercase tracking-wider font-medium">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Community screenshot cascade → click to open gallery */}
              <Gallery />
            </div>
          </section>

          {/* ─── HOW TO JOIN ─── */}
          <section id="how-to-join" className="py-16 scroll-mt-20">
            <SectionHeading subtitle="Joining HVY is a simple process, but there are a few basic requirements you must meet to become a member.">
              How to Join
            </SectionHeading>

            {/* Rule cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {[
                {
                  icon: (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                    </svg>
                  ),
                  title: '18+ Community',
                  desc: 'HVY is an 18+ community. While we recognize that maturity isn\'t solely defined by age, this rule is in place to maintain the desired level of engagement and professionalism.',
                },
                {
                  icon: (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                    </svg>
                  ),
                  title: 'No Multi-Clanning',
                  desc: 'To honor the effort invested in building this community, we require members to commit fully by not joining other DCS flying groups. This policy ensures loyalty and dedication within HVY.',
                },
                {
                  icon: (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  ),
                  title: 'Zero Tolerance Policy',
                  desc: 'HVY strictly prohibits hate speech, racism, and related behavior. Applicants who cannot adhere to this standard should not apply.',
                },
              ].map((rule) => (
                <div
                  key={rule.title}
                  className="bg-white/60 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-200/50 hover:shadow-md transition-shadow duration-300"
                >
                  <div className="w-12 h-12 rounded-lg bg-hvy-500/10 text-hvy-500 flex items-center justify-center mb-4">
                    {rule.icon}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{rule.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{rule.desc}</p>
                </div>
              ))}
            </div>

            {/* Application steps */}
            <div className="mt-12 bg-white/60 backdrop-blur-sm rounded-xl p-8 shadow-sm border border-gray-200/50">
              <h3 className="font-heading text-2xl font-bold uppercase text-hvy-900 mb-6">Application Process</h3>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-0">
                {[
                  { step: '1', text: 'Join our Discord using the invite link below' },
                  { step: '2', text: 'Navigate to the #new-applications channel' },
                  { step: '3', text: 'Type /apply to complete your application via PirateBot' },
                ].map((item, i) => (
                  <div key={item.step} className="flex items-center flex-1">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-10 h-10 rounded-full bg-hvy-500 text-white font-bold flex items-center justify-center flex-shrink-0">
                        {item.step}
                      </div>
                      <p className="text-gray-700">{item.text}</p>
                    </div>
                    {i < 2 && (
                      <svg className="hidden md:block w-6 h-6 text-gray-300 mx-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 text-center">
                <a
                  href="https://discord.gg/sPavY3ZZQH"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackJoinDiscord('home')}
                  className="inline-block bg-discord hover:bg-discord/90 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 uppercase tracking-wider"
                >
                  Join Our Discord
                </a>
              </div>
            </div>
          </section>

          {/* ─── FAQ ─── */}
          <FAQ />

          {/* ─── SQUADRONS ─── */}
          <section id="squadrons" className="py-16 scroll-mt-20">
            <SectionHeading subtitle="Joint Task Force Heavy has several squadrons, each with their own unique airframe and mission set. Those squadrons belong to three main battle groups, plus our Auxiliary Corps.">
              Our Squadrons
            </SectionHeading>

            {GROUPS.map((group) => (
              <div key={group.name} className="mb-14">
                <h3 className="font-heading text-2xl font-bold uppercase text-hvy-900 mb-6 flex items-center gap-3">
                  <span className="h-px flex-1 bg-gray-300/50 max-w-[3rem]" />
                  {group.name}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {group.squadrons.map((sq) => (
                    <Link key={sq.slug} to={`/squadrons/${sq.slug}`} className="block">
                      <SquadronCard img={sq.logo} alt={`${sq.name} insignia`} name={sq.name} airframe={sq.airframeShort} />
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            <p className="mt-2 text-lg text-gray-700">
              Browse all of{' '}
              <Link to="/squadrons" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">
                our DCS squadrons
              </Link>{' '}
              by airframe and battle group to find the unit you want to fly with.
            </p>
          </section>

          {/* ─── DISCORD / JOIN ─── */}
          <section id="join-discord" className="py-16 scroll-mt-20">
            <SectionHeading>Join Our Community</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Discord Widget - lazy loaded */}
              <div className="bg-hvy-900 rounded-xl shadow-lg p-6 flex justify-center items-center min-h-[532px]">
                <LazyLoad>
                  <iframe
                    src="https://discord.com/widget?id=398677618985009152&theme=dark"
                    width="350"
                    height="500"
                    allowTransparency="true"
                    frameBorder="0"
                    sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
                    title="Discord Widget"
                    className="rounded-lg"
                  ></iframe>
                </LazyLoad>
              </div>

              {/* CTA */}
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-8 shadow-sm border border-gray-200/50 flex flex-col justify-center items-center text-center">
                <div className="w-16 h-16 rounded-full bg-discord/10 text-discord flex items-center justify-center mb-6">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z"/>
                  </svg>
                </div>
                <h3 className="font-heading text-2xl font-bold uppercase text-gray-900 mb-3">Join the Conversation</h3>
                <p className="text-gray-600 mb-2">
                  Connect with our pilots in an active Discord community.
                </p>
                <p className="text-sm text-gray-400 mb-6">
                  Flight ops, training, and camaraderie await.
                </p>
                <a
                  href="https://discord.gg/sPavY3ZZQH"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackJoinDiscord('home')}
                  className="inline-block bg-discord hover:bg-discord/90 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 uppercase tracking-wider mb-6"
                >
                  Join Our Discord
                </a>
                {/* Social links */}
                <div className="flex items-center gap-4 pt-4 border-t border-gray-200/50">
                  <a href="https://www.youtube.com/@jointtaskforceheavy" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-red-500 transition-colors" aria-label="YouTube">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  </a>
                  <a href="https://www.facebook.com/jointtaskforceheavy/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600 transition-colors" aria-label="Facebook">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  <a href="https://www.instagram.com/jointtaskforceheavy/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-500 transition-colors" aria-label="Instagram">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
                  </a>
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* ═══════════════ BACK TO TOP BUTTON ═══════════════ */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-hvy-accent hover:bg-hvy-gold text-hvy-900 shadow-lg flex items-center justify-center transition-all duration-300 ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label="Back to top"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
        </svg>
      </button>
    </div>
  );
};

export default MainContent;
